/**
 * Turning a field path into something a person would write on a tile.
 *
 * This is the TypeScript twin of `charting-backend/src/chart_engine/labels.py`,
 * and it exists because the same defect was written twice in two languages. The
 * backend did `leaf.title()`; the frontend did
 * `leaf.replace(/\b\w/g, m => m.toUpperCase())`. Both are right for
 * `created_at` and wrong for every camelCase field, because neither splits camel
 * humps — `\b` matches only at the start of `priceChangePercent`, so it became
 * "PriceChangePercent", and the table's `textTransform: uppercase` finished the
 * job as **PRICECHANGEPERCENT** in a live board's column header.
 *
 * Keep the two implementations in step: their tests use the same field names on
 * purpose, so a change to one that is not mirrored shows up as a disagreement.
 */

/** Words that are wrong when title-cased. Deliberately short: a long list of
 *  "smart" replacements is how labels start disagreeing with the data
 *  dictionary. Mirrors `_KEEP` in labels.py. */
const KEEP: Record<string, string> = {
  id: 'ID', uuid: 'UUID', url: 'URL', uri: 'URI', api: 'API',
  sku: 'SKU', kpi: 'KPI', usd: 'USD', eur: 'EUR', inr: 'INR',
  ctr: 'CTR', roi: 'ROI', arr: 'ARR', mrr: 'MRR', csat: 'CSAT',
  sla: 'SLA', ai: 'AI', jd: 'JD', qa: 'QA', eta: 'ETA',
  pct: '%', percent: '%', perc: '%', avg: 'Avg', num: 'Number',
  qty: 'Quantity', amt: 'Amount', ts: 'Timestamp', iso: 'ISO',
};

/** Hops that carry no meaning for a reader. Mirrors `_NOISE`. */
const NOISE = new Set(['', 'data', 'item', 'items', 'attributes', 'fields']);

/** Separates PATH HOPS. Not `_` or `-`: those separate WORDS inside one
 *  segment, and treating them as hops turns `created_at` into "At". */
const PATH = /[.[\]/]+/;
/** Separates WORDS inside a segment. */
const WORDS = /[_\-\s]+/;
/** A camel hump, and the ACRONYMBoundary case (`HTTPStatus` -> HTTP, Status). */
const HUMPS = /(?<=[a-z0-9])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/;

function segments(field: string): string[] {
  return String(field ?? '').split(PATH).filter(Boolean);
}

function wordsOf(segment: string): string[] {
  return segment.split(WORDS).flatMap((chunk) => chunk.split(HUMPS)).filter(Boolean);
}

/** The last meaningful segment of a field path. */
export function fieldLeaf(field: string): string {
  const parts = segments(field);
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (!NOISE.has(parts[i].toLowerCase())) return parts[i];
  }
  return parts.length ? parts[parts.length - 1] : '';
}

/**
 * A label for `field`.
 *
 * `humanizeField('[].priceChangePercent')` -> 'Price Change %'
 * `humanizeField('created_at')`            -> 'Created At'
 * `humanizeField('coins[].item.data.content')` -> 'Content'
 */
export function humanizeField(field: string, opts: { fullPath?: boolean } = {}): string {
  const source = opts.fullPath ? String(field ?? '') : fieldLeaf(field);
  let parts = (opts.fullPath ? segments(source) : [source])
    .filter((s) => !NOISE.has(s.toLowerCase()))
    .flatMap(wordsOf);
  if (!parts.length) {
    // Every segment was a wrapper (`data[].item`). Better the wrapper's own
    // name than an empty header.
    const raw = segments(field);
    if (!raw.length) return '';
    parts = wordsOf(raw[raw.length - 1]);
    if (!parts.length) return '';
  }
  const out = parts.map((w) => {
    const fixed = KEEP[w.toLowerCase()];
    if (fixed !== undefined) return fixed;
    if (w.length > 1 && w === w.toUpperCase()) return w;  // an acronym as written
    return w.charAt(0).toUpperCase() + w.slice(1);
  });
  return out.join(' ').replace(/\s+%/g, ' %').trim();
}
